<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type {
    Project,
    ProjectsByStatus,
    ProjectStatus,
    CalendarEvent,
    MonthRange,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getMonthDisplayName,
    getShortMonthName,
    isCurrentMonth,
    isFutureMonth,
    categoryIcons,
    statusAccentColors,
    eventsForMonth,
    parseCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip } from '../lib/stores/projectTooltip';
  import {
    openProjectRail,
    openProjectModalForCreate,
    requestProjectMove,
    openEventModal,
    openEventModalForEdit,
  } from '../lib/stores/ui';
  import { useExpandableProjects } from '../lib/useExpandableProjects.svelte';
  import { useProjectDragDrop } from '../lib/useProjectDragDrop.svelte';
  import ExpandableProjectChip from './ExpandableProjectChip.svelte';

  interface Props {
    visibleMonths: string[];
    projectsByMonth: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentMonth: string;
    range: MonthRange;
    display?: DisplayFilter | null;
  }

  let {
    visibleMonths,
    projectsByMonth,
    events,
    currentMonth,
    range = 'quarter',
    display = null,
  }: Props = $props();

  // Range options (ordered: 1 month, quarter, 3 months, 6 months, year)
  const rangeOptions: { value: MonthRange; label: string }[] = [
    { value: '1-month', label: '1 Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: '3-months', label: '3 months' },
    { value: '6-months', label: '6 months' },
    { value: 'year', label: 'Year' },
  ];

  function setRange(newRange: MonthRange) {
    if (newRange === range) return;

    const params = new URLSearchParams();
    params.set('view', 'rolling');
    if (newRange !== 'quarter') {
      params.set('range', newRange);
    }

    router.get(`/month/${currentMonth}?${params.toString()}`, {}, { preserveScroll: true });
  }

  // Get projects by status for a month
  function getProjects(monthKey: string, status: ProjectStatus): Project[] {
    const data = projectsByMonth[monthKey] || { planned: [], active: [], almost: [], done: [] };
    return data[status] || [];
  }

  // Get all projects for a month
  function getAllProjects(monthKey: string): Project[] {
    const data = projectsByMonth[monthKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  // Get project counts
  function getProjectCounts(monthKey: string): {
    planned: number;
    active: number;
    done: number;
    total: number;
  } {
    const data = projectsByMonth[monthKey] || { planned: [], active: [], almost: [], done: [] };
    return {
      planned: data.planned?.length || 0,
      active: data.active?.length || 0,
      done: data.done?.length || 0,
      total: (data.planned?.length || 0) + (data.active?.length || 0) + (data.done?.length || 0),
    };
  }

  // Get events for a specific month
  function getMonthEvents(monthKey: string): CalendarEvent[] {
    return sortEventsForDisplay(eventsForMonth(events, monthKey));
  }

  // Format event date with abbreviated weekday (e.g., "Mo 15")
  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  function formatEventDateWithWeekday(dateStr: string): string {
    const date = parseCalendarDate(dateStr);
    const weekday = weekdays[date.getUTCDay()];
    const day = date.getUTCDate();
    return `${weekday} ${day}`;
  }

  // Get first day of month for creating events
  function getMonthFirstDate(monthKey: string): string {
    return `${monthKey}-01`;
  }

  // Month state
  function getMonthState(monthKey: string): 'past' | 'current' | 'future' {
    if (isCurrentMonth(monthKey)) return 'current';
    if (isFutureMonth(monthKey)) return 'future';
    return 'past';
  }

  // Navigate to month focus view
  function navigateToMonth(monthKey: string) {
    router.get(`/month/${monthKey}?view=focus`, {}, { preserveScroll: true });
  }

  function handleAddProject(monthKey: string) {
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: monthKey,
      status: 'planned',
    });
  }

  // Expand/collapse state (shared utility)
  const expander = useExpandableProjects(() => getAllProjects(currentMonth));
  // eslint-disable-next-line momentium/no-unguarded-effect -- idempotent expander init: runs once via hasInitialized guard, needs reactive project data
  $effect(() => expander.initializeExpansion());

  // Drag and drop state (shared utility)
  const dnd = useProjectDragDrop((projectId, project, monthKey) => {
    requestProjectMove(projectId, project, 'month', { monthKey, status: 'planned' });
  });
</script>

<div class="rolling-container">
  <!-- Header with range selector -->
  <div class="rolling-header">
    <span class="header-title">Rolling Months</span>
    <div class="range-selector">
      {#each rangeOptions as opt}
        <button
          type="button"
          class="range-btn"
          class:active={range === opt.value}
          onclick={() => setRange(opt.value)}
        >
          {opt.label}
        </button>
      {/each}
    </div>
  </div>

  <!-- Month cards stack -->
  <div class="months-stack">
    {#each visibleMonths as monthKey (monthKey)}
      {@const state = getMonthState(monthKey)}
      {@const isCurrent = state === 'current'}
      {@const isPast = state === 'past'}
      {@const isFuture = state === 'future'}
      {@const projects = getAllProjects(monthKey)}
      {@const counts = getProjectCounts(monthKey)}
      {@const monthEvents = getMonthEvents(monthKey)}
      {@const allExpanded = expander.areAllExpanded(projects)}
      {@const hasExpandable = projects.length > 0}

      <div
        class="month-card"
        class:past={isPast}
        class:current={isCurrent}
        class:future={isFuture}
        class:drop-target={dnd.dropTarget === monthKey}
        role="group"
        ondragover={(e) => dnd.handleDragOver(e, monthKey)}
        ondragleave={dnd.handleDragLeave}
        ondrop={(e) => dnd.handleDrop(e, monthKey)}
      >
        <!-- Month header -->
        <div class="month-header">
          <button type="button" class="month-title-btn" onclick={() => navigateToMonth(monthKey)}>
            <span class="month-name">{getMonthDisplayName(monthKey)}</span>
            {#if isCurrent}
              <span class="current-badge">This Month</span>
            {/if}
          </button>

          {#if hasExpandable}
            <button
              type="button"
              class="expand-toggle-btn"
              class:all-expanded={allExpanded}
              onclick={() =>
                allExpanded
                  ? expander.collapseGroup(getAllProjects(monthKey))
                  : expander.expandGroup(getAllProjects(monthKey))}
            >
              {#if allExpanded}
                <span class="toggle-icon">▼</span>
                <span class="toggle-text">Collapse</span>
              {:else}
                <span class="toggle-icon">▶</span>
                <span class="toggle-text">Expand</span>
              {/if}
            </button>
          {/if}

          <button
            type="button"
            class="add-btn"
            onclick={() => handleAddProject(monthKey)}
            title="Add project to this month"
          >
            +
          </button>
        </div>

        <!-- Month content -->
        <div class="month-content">
          <!-- Projects grid -->
          {#if showProjects(display)}
            <div class="projects-area">
              {#if projects.length > 0}
                <div class="projects-grid" class:expanded={isCurrent}>
                  {#each projects as project (project.id)}
                    <ExpandableProjectChip
                      {project}
                      expanded={expander.isProjectExpanded(project.id)}
                      onToggleExpand={() => expander.toggleProjectExpand(project.id)}
                      isDragging={dnd.draggingProjectId === project.id}
                      onDragStart={(e) => dnd.handleDragStart(e, project, monthKey)}
                      onDragEnd={dnd.handleDragEnd}
                    />
                  {/each}
                </div>
              {:else}
                <div class="empty-month">
                  {isFuture ? 'No targets planned' : 'No projects'}
                </div>
              {/if}
            </div>
          {/if}

          <!-- Events sidebar -->
          {#if showEvents(display)}
            <div class="events-sidebar">
              <div class="events-header">
                <span>📅 Events ({monthEvents.length})</span>
                <button
                  type="button"
                  class="events-add-btn"
                  onclick={() => openEventModal(getMonthFirstDate(monthKey))}
                  title="Add event"
                >
                  +
                </button>
              </div>
              {#if monthEvents.length > 0}
                <div class="events-list">
                  {#each monthEvents as event (event.id)}
                    {@const timeStr = formatEventTimeShort(event)}
                    <button
                      type="button"
                      class="event-item event-{event.type}"
                      onclick={() => openEventModalForEdit(event)}
                      title="Click to edit"
                    >
                      <span class="event-date"
                        >{formatEventDateWithWeekday(event.date)}{#if timeStr}
                          · {timeStr}{/if}</span
                      >
                      <span class="event-title">{event.title}</span>
                    </button>
                  {/each}
                </div>
              {:else}
                <div class="events-empty">No events this month</div>
              {/if}
            </div>
          {/if}
        </div>
      </div>
    {/each}
  </div>
</div>

<style>
  .rolling-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.5rem;
    gap: 0.5rem;
    overflow: hidden;
    background: #f9fafb;
  }

  /* Header */
  .rolling-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.5rem;
    background: white;
    border-radius: 0.375rem;
    flex-shrink: 0;
  }

  .header-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .range-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
  }

  .range-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .range-btn:hover {
    color: #374151;
    background: white;
  }

  .range-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Months stack */
  .months-stack {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    overflow-y: auto;
    padding-right: 0.25rem;
  }

  .month-card {
    background: white;
    border-radius: 0.375rem;
    overflow: hidden;
    transition: all 0.15s;
    flex-shrink: 0;
  }

  .month-card.past {
    background: #fafafa;
    box-shadow: inset 0 0 0 1px #e5e7eb;
  }

  .month-card.current {
    background: white;
    box-shadow:
      0 2px 8px rgba(59, 130, 246, 0.15),
      inset 0 0 0 2px #3b82f6;
  }

  .month-card.future {
    background: #fefefe;
    box-shadow: inset 0 0 0 1px #e9d5ff;
  }

  .month-card.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6 !important;
  }

  /* Month header */
  .month-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.5rem;
    border-bottom: 1px solid #f3f4f6;
  }

  .month-card.current .month-header {
    background: #eff6ff;
    border-bottom-color: #bfdbfe;
  }

  .month-title-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.125rem;
    border-radius: 0.25rem;
    transition: background 0.15s;
  }

  .month-title-btn:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .month-name {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .current-badge {
    font-size: 0.5625rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
  }

  .expand-toggle-btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: none;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    margin-left: auto;
  }

  .expand-toggle-btn:hover {
    color: #3b82f6;
    border-color: #3b82f6;
    background: #eff6ff;
  }

  .expand-toggle-btn.all-expanded {
    color: #3b82f6;
    border-color: #93c5fd;
    background: #eff6ff;
  }

  .toggle-icon {
    font-size: 0.5rem;
    transition: transform 0.15s ease;
  }

  .toggle-text {
    line-height: 1;
  }

  .add-btn {
    width: 1.25rem;
    height: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 500;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .add-btn:hover {
    color: #3b82f6;
    background: #dbeafe;
  }

  /* Month content */
  .month-content {
    display: flex;
    padding: 0.375rem 0.5rem;
    gap: 0.5rem;
  }

  .month-card.current .month-content {
    padding: 0.5rem;
  }

  .projects-area {
    flex: 1;
    min-width: 0;
  }

  .projects-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: flex-start;
    align-content: flex-start;
  }

  .projects-grid.expanded {
    gap: 0.375rem;
  }

  /* Expandable chip sizing within grid - consistent width everywhere */
  .projects-grid :global(.expandable-chip) {
    width: 15rem;
    max-width: 15rem;
    flex-shrink: 0;
  }

  .empty-month {
    font-size: 0.625rem;
    color: #9ca3af;
    padding: 0.25rem;
  }

  /* Events sidebar */
  .events-sidebar {
    width: 15rem;
    height: 11.875rem;
    flex-shrink: 0;
    background: #faf5ff;
    border-radius: 0.25rem;
    padding: 0.375rem;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .events-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.625rem;
    font-weight: 600;
    color: #7c3aed;
    margin-bottom: 0.25rem;
    flex-shrink: 0;
  }

  .events-add-btn {
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 500;
    color: #7c3aed;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .events-add-btn:hover {
    color: #7c3aed;
    background: #ede9fe;
    border-color: #7c3aed;
  }

  .events-list {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
    overflow-y: auto;
    flex: 1;
    min-height: 0;

    /* Hide scrollbar until hover */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  .events-list::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }

  .events-list::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }

  .events-list:hover {
    scrollbar-width: thin; /* Firefox */
  }

  .events-list:hover::-webkit-scrollbar-thumb {
    background: #d1d5db;
  }

  .events-list:hover::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  .event-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.375rem;
    padding-left: 0.5rem;
    background: #fefefe;
    border: none;
    border-radius: 0.125rem;
    font-size: 0.5625rem;
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.15s;
  }

  .event-item:hover {
    background: #f3f4f6;
  }

  .event-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #9ca3af;
    border-radius: 0.125rem 0 0 0.125rem;
  }

  /* Event type accent colors */
  .event-item.event-appointment::before {
    background: #60a5fa; /* blue-400 */
  }

  .event-item.event-travel::before {
    background: #a78bfa; /* purple-400 */
  }

  .event-item.event-event::before {
    background: #4ade80; /* green-400 */
  }

  .event-item.event-deadline::before {
    background: #f87171; /* red-400 */
  }

  .event-date {
    font-size: 0.5rem;
    font-weight: 500;
    color: #4b5563;
    background: #f3f4f6;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.125rem;
    flex-shrink: 0;
    font-family: ui-monospace, monospace;
    letter-spacing: -0.02em;
  }

  .event-title {
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .events-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    color: #9ca3af;
    font-style: italic;
  }
</style>
